// SOF-838: live carrier-terminated flag from CarrierTerminatedPlan takes precedence over the
// sync-cached isTermed/terminationType pair, so classification works before a client resync.
export const isEffectivelyCarrierTerminated = (rate) =>
  rate?.isCarrierTerminated === true ||
  (rate?.isTermed === true && rate?.terminationType === "carrier");

export const isEffectivelyTermed = (rate) =>
  isEffectivelyCarrierTerminated(rate) ||
  (rate?.isTermed === true && rate?.terminationType === "client");

export const hasAnyTermedPlans = (flatRates) =>
  flatRates.some((r) => isEffectivelyTermed(r));

export const calcNewPremiumTotal = (flatRates) =>
  flatRates
    .filter((r) => !isEffectivelyTermed(r))
    .reduce((sum, r) => sum + r.newTotalPremium * r.eeCount, 0);

export const partitionByTermination = (flatRates) => ({
  active: flatRates.filter((r) => !isEffectivelyTermed(r)),
  carrierTermed: flatRates.filter((r) => isEffectivelyCarrierTerminated(r)),
  clientTermed: flatRates.filter(
    (r) => r.isTermed && r.terminationType === "client" && !isEffectivelyCarrierTerminated(r)
  ),
});

// Collapse per-(group,plan) rows into one row per plan for summary rollups.
// - Dedup key: planId when present, else planName + planType.
// - currentMonthlyPremium is summed across duplicates (client-wide monthly cost).
// - Other fields (planName, planType, terminationType, mappedRenewalPlanName, ...)
//   take the first-seen value; mappedRenewalPlanName prefers the first non-empty.
export const dedupTermedByPlan = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const byKey = new Map();
  for (const r of rows) {
    const key = r.planId ?? `${r.planName ?? ""}|${r.planType ?? ""}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...r, currentMonthlyPremium: r.currentMonthlyPremium || 0 });
      continue;
    }
    existing.currentMonthlyPremium =
      (existing.currentMonthlyPremium || 0) + (r.currentMonthlyPremium || 0);
    if (!existing.mappedRenewalPlanName && r.mappedRenewalPlanName) {
      existing.mappedRenewalPlanName = r.mappedRenewalPlanName;
    }
  }
  return Array.from(byKey.values());
};

// Collapse ClientTermedPlanUIModel entries (soft-deleted rate rows) to one card per plan.
// The API fans out one entry per (group, plan, tier); this rollup sums originalPremium and
// comparisonPremium, prefers the first non-empty replacement mapping, and OR-s the has* flags.
export const dedupDeletedTermedPlansByPlan = (plans) => {
  if (!Array.isArray(plans) || plans.length === 0) return [];
  const byKey = new Map();
  for (const p of plans) {
    const key = p.planId ?? `${p.planName ?? ""}|${p.planType ?? ""}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        planId: p.planId,
        planName: p.planName,
        planType: p.planType,
        planTypeDescription: p.planTypeDescription,
        planOfferType: p.planOfferType,
        originalPremium: p.originalPremium ?? 0,
        hasOriginalPremium: p.hasOriginalPremium === true,
        comparisonPremium: p.comparisonPremium ?? 0,
        hasComparisonPremium: p.hasComparisonPremium === true,
        lastKnownPremium: p.lastKnownPremium ?? null,
        hasLastKnownPremium: p.hasLastKnownPremium === true,
        replacementPlanId: p.replacementPlanId || null,
        replacementPlanName: p.replacementPlanName || null,
        hasReplacementPlan: p.hasReplacementPlan === true,
        isSelectable: p.isSelectable === true,
      });
      continue;
    }
    existing.originalPremium =
      (existing.originalPremium || 0) + (p.originalPremium || 0);
    existing.hasOriginalPremium =
      existing.hasOriginalPremium || p.hasOriginalPremium === true;
    existing.comparisonPremium =
      (existing.comparisonPremium || 0) + (p.comparisonPremium || 0);
    existing.hasComparisonPremium =
      existing.hasComparisonPremium || p.hasComparisonPremium === true;
    if (existing.lastKnownPremium == null && p.lastKnownPremium != null) {
      existing.lastKnownPremium = p.lastKnownPremium;
    }
    existing.hasLastKnownPremium =
      existing.hasLastKnownPremium || p.hasLastKnownPremium === true;
    if (!existing.replacementPlanId && p.replacementPlanId) {
      existing.replacementPlanId = p.replacementPlanId;
    }
    if (!existing.replacementPlanName && p.replacementPlanName) {
      existing.replacementPlanName = p.replacementPlanName;
    }
    existing.hasReplacementPlan =
      existing.hasReplacementPlan || p.hasReplacementPlan === true;
  }
  return Array.from(byKey.values());
};

export const calcGroupProjectedCost = (rates) =>
  rates
    .filter((r) => !isEffectivelyTermed(r))
    .reduce((sum, r) => {
      if (r.percentSelected)
        return sum + (r.percentValue ?? 0) * r.newTotalPremium * r.eeCount;
      if (r.fixedValueSelected)
        return sum + (r.fixedValue ?? 0) * r.eeCount;
      return sum;
    }, 0);

export const isGroupPending = (rates) =>
  rates
    .filter((r) => !isEffectivelyTermed(r))
    .some((r) => !r.percentSelected && !r.fixedValueSelected);

// SOF-826: sentinel shown when a rollup/percentage cannot be reliably produced.
// Never substitute $0 or a fabricated value.
export const NOT_AVAILABLE = "N/A";

// New-premium total that returns null (=> N/A) when there are no active plans to sum,
// e.g. when the only rows are carrier-terminated. Terminated rows never contribute.
export const calcNewPremiumTotalOrNa = (flatRates) => {
  const active = flatRates.filter((r) => !isEffectivelyTermed(r));
  if (active.length === 0) return null;
  return active.reduce((sum, r) => sum + r.newTotalPremium * r.eeCount, 0);
};

// Percentage rate change that returns null (=> N/A) when it cannot be reliably computed:
// a missing or zero baseline (which is what happens when the only comparable premium would
// come from a carrier-terminated plan). Returns a fraction (e.g. 0.12 for +12%).
export const calcRateChangePercentOrNa = (currentTotal, newTotal) => {
  if (
    currentTotal === null ||
    currentTotal === undefined ||
    newTotal === null ||
    newTotal === undefined ||
    currentTotal === 0
  ) {
    return null;
  }
  return (newTotal - currentTotal) / currentTotal;
};

// Renders a computed rollup value, or the N/A sentinel when the value is null/undefined.
export const formatRollupOrNa = (value, formatter) =>
  value === null || value === undefined
    ? NOT_AVAILABLE
    : formatter(value);
